import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getJwtToken, getToken } from '../../../services/auth';
import { signInSuccess } from '../../../redux/actions/authActions';
import { getUserAccessPermissions } from '../../../redux/UserManagement/actions';
import { parseJwt } from '../../../utils/utility';

export const useAuthCheck = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const authState = useSelector((state: any) => state.auth);
  const isAuthenticated = authState?.isAuthenticated || false;

  // If ProtectedRoute sent us here, it stashed the original URL in location.state.from.
  // Fall back to /Projects when no redirect origin is available.
  const getPostLoginDestination = (): string => {
    const from = (location.state as { from?: { pathname?: string; search?: string; hash?: string } } | null)?.from;
    if (from?.pathname && from.pathname !== '/login' && from.pathname !== '/reset-password') {
      return `${from.pathname}${from.search ?? ''}${from.hash ?? ''}`;
    }
    return '/Projects';
  };

  const navigateBasedOnUserGroup = (userGroups: string[]) => {
    const destination = getPostLoginDestination();
    if (userGroups?.includes('web-page') || userGroups?.includes('web-page-without-create-project') || userGroups?.includes('super-admin')) {
      navigate(destination);
    }
    // else if (userGroups?.includes('admin')) {
    //   navigate('/admin');
    // }
    // else if (['heat-profile', 'coating-record-db', 'dcal-generator'].some(group => userGroups?.includes(group))) {
    //   navigate('/technical');
    // }
    // else if (userGroups?.includes('analysis')) {
    //   navigate('/log-report');
    // }
    else {
      navigate(destination);
    }
  };

  useEffect(() => {
    const checkAuthStatus = async () => {
      let token = getToken();
      
      if (token && !isAuthenticated) {
        // User has a token but Redux state doesn't reflect it
        // This could happen on page refresh
        try {
          // Parse the JWT token to get user information
          let userPayload = parseJwt(token);
          
          // If the saved ID token expired, refresh it with Cognito before logging out.
          if (userPayload.exp < new Date().getTime() / 1000) {
            const refreshedToken = await getJwtToken();
            if (!refreshedToken) {
              throw new Error('Unable to refresh authentication session');
            }

            token = refreshedToken;
            userPayload = parseJwt(refreshedToken);
          }

          // Extract user info from token payload
          const user = {
            email: userPayload.email,
            sub: userPayload.sub,
            groups: userPayload['cognito:groups']
          };


          // Dispatch the Redux action to fetch user permissions
          dispatch(getUserAccessPermissions(user.sub));

          // Dispatch signInSuccess to restore authentication state
          dispatch(signInSuccess({ user, token }));
          navigate(getPostLoginDestination());

        } catch (error) {
          // Clear invalid token
          localStorage.removeItem('ap_idToken');
          localStorage.removeItem('ap_rToken');
          localStorage.removeItem('user');
          navigate('/login');
        }
      } else if (isAuthenticated) {
        // User is already authenticated, navigate based on user group
        const userGroups = authState?.user?.groups || [];
        navigateBasedOnUserGroup(userGroups);
           // Dispatch the Redux action to fetch user permissions
          // dispatch(getUserAccessPermissions(authState.us));
      } else if (!token && !isAuthenticated) {
        try {
          const refreshedToken = await getJwtToken();
          if (!refreshedToken) {
            throw new Error('Unable to refresh authentication session');
          }

          const userPayload = parseJwt(refreshedToken);
          const user = {
            email: userPayload.email,
            sub: userPayload.sub,
            groups: userPayload['cognito:groups']
          };

          dispatch(getUserAccessPermissions(user.sub));
          dispatch(signInSuccess({ user, token: refreshedToken }));
          navigate(getPostLoginDestination());
        } catch (error) {
          localStorage.removeItem('ap_idToken');
          localStorage.removeItem('ap_rToken');
          localStorage.removeItem('user');
          navigate('/login');
        }
      }
    };

    checkAuthStatus();
  }, [isAuthenticated, navigate, dispatch, authState?.user?.groups]);

  // Navigate after successful authentication restoration
  useEffect(() => {
    if (isAuthenticated && authState?.user) {
      const userGroups = authState.user.groups || [];
      // Add a small delay to ensure everything is properly loaded
      setTimeout(() => {
        navigateBasedOnUserGroup(userGroups);
      }, 100);
    }
  }, [isAuthenticated, authState?.user, navigate]);

  return {
    isAuthenticated,
    isLoading: authState?.loading || false
  };
}; 